import axios from 'axios';
import { isObject } from 'lodash';

import Article from '../models/Article';
import Comment from '../models/Comment';
import Subcomment from '../models/Subcomment';
import TrimmedUser from '../models/TrimmedUser';
import User from '../models/User';
import FlatComment from '../models/FlatComment';
import FileData from '../models/FileData';
import Wod from '../models/Wod';
import WorkoutTypeInstance from '../models/WorkoutTypeInstance';
import WorkoutResult from '../models/WorkoutResult';
import WorkoutBenchmarkResult from '../models/WorkoutBenchmarkResult';
import WorkoutResultForAdmin from '../models/WorkoutResultForAdmin';
import Benchmark from '../models/Benchmark';

/**
 * Passes workout result data into FE model.
 *
 * Check if result user is populated to determine
 * which FE model to load into. User field is required
 * so it's safe to perform that check on it.
 * @param {Object} workoutResult workout result data
 */
const handleUnknownWorkoutResultType = (workoutResult) => {
  if (isObject(workoutResult.user)) {
    return new WorkoutResultForAdmin(workoutResult);
  }

  return new WorkoutResult(workoutResult);
};

class Api {
  constructor(baseURL) {
    this.axios = axios.create({
      baseURL: `${baseURL}/api`,
      withCredentials: true,
    });

    this.axios.interceptors.response.use(
      (response) => response,
      (err) => {
        if (
          err.response.data.name === 'ApiError' &&
          err.response.data.code === 401
        ) {
          localStorage.clear();
          window.location.reload();
        }
        return Promise.reject(err);
      }
    );
  }

  /**
   * Pulls out response data and resolves promise.
   * @param {Object} res axios response
   */
  handleSuccess(res) {
    return Promise.resolve(res.data);
  }

  /**
   * Pulls out response data,
   * creates data model instance list
   * and returns it with pagination info
   * parsed from res headers.
   * @param {*} res axios response
   * @param {*} Model fe model to map data into
   */
  handlePaginatedListSuccess(res, Model) {
    let { page, limit, pages, total } = res.headers;
    const modelInstanceList = res.data.map((modelData) => new Model(modelData));
    return [
      modelInstanceList,
      {
        page: parseInt(page, 10),
        limit: parseInt(limit, 10),
        pages: parseInt(pages, 10),
        total: parseInt(total, 10),
      },
    ];
  }

  /**
   * Rejects promise with error containing messages to show to the end user.
   *
   * Either base or fieldName is present.
   * @param {Error} err axios response error
   * @returns Error {
   *  errors: {
   *    base: String // optional, contains generic errors
   *    [fieldName]: String // optional, contains field errors
   *  }
   * }
   */
  handleError(err) {
    const error = new Error();
    if (err.response && err.response.data) {
      const { data } = err.response;
      if (data.name === 'ValidationError') {
        // field validation error
        error.errors = data.errors;
      } else if (data.name === 'ApiError') {
        // generic api error
        error.errors = { base: data.message };
      }
    } else {
      // network or internal server errors
      error.errors = { base: 'Неизвестна грешка' };
    }

    return Promise.reject(error);
  }

  login({ email, password }) {
    return this.axios
      .post('/login', {
        email,
        password,
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  logout() {
    return this.axios
      .post('/logout')
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  requestResetPassword(email) {
    return this.axios
      .post('/users/password/request-reset', {
        email,
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  resetPassword(userCode, password) {
    return this.axios
      .post('/users/password/reset', {
        userCode,
        password,
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  activateUser(userCode, password) {
    return this.axios
      .post('/users/activate', {
        userCode,
        password,
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  getUser(userId = 'me') {
    return this.axios
      .get(`/users/${userId}`)
      .then(this.handleSuccess)
      .then((user) => new User(user))
      .catch(this.handleError);
  }

  inviteUser(userData) {
    return this.axios
      .post('/users', userData)
      .then(this.handleSuccess)
      .then((user) => new User(user))
      .catch(this.handleError);
  }

  editUser(userData) {
    return this.axios
      .put(`/users/${userData.id}`, userData)
      .then(this.handleSuccess)
      .then((user) => new User(user))
      .catch(this.handleError);
  }

  markUserWod(wodId, mark) {
    return this.axios
      .post(`/wods/${wodId}/completed`, { mark })
      .then(this.handleSuccess)
      .then((user) => new User(user))
      .catch(this.handleError);
  }

  getUsers(headers, searchTerm) {
    const qs = new URLSearchParams();
    if (searchTerm) {
      qs.append('term', searchTerm);
    }
    return this.axios
      .get(`/users?${qs}`, { headers })
      .then((res) => this.handlePaginatedListSuccess(res, User))
      .catch(this.handleError);
  }

  getTrimmedUsers(headers, searchTerm, role) {
    let params = {};
    if (searchTerm) params.term = searchTerm;
    if (role) params.role = role;
    return this.axios
      .get('/users', { headers, params })
      .then((res) => this.handlePaginatedListSuccess(res, TrimmedUser))
      .catch(this.handleError);
  }

  getArticles() {
    return this.axios
      .get('/articles')
      .then(this.handleSuccess)
      .then((articles) => articles.map((article) => new Article(article)))
      .catch(this.handleError);
  }

  getPublishedArticles() {
    return this.axios
      .get('/articles/published')
      .then(this.handleSuccess)
      .then((articles) => articles.map((article) => new Article(article)))
      .catch(this.handleError);
  }

  getArticle(articleId) {
    return this.axios
      .get(`/articles/${articleId}`)
      .then(this.handleSuccess)
      .then((article) => new Article(article))
      .catch(this.handleError);
  }

  updateArticle(articleId, articleData) {
    return this.axios
      .put(`/articles/${articleId}`, articleData)
      .then(this.handleSuccess)
      .then((article) => new Article(article))
      .catch(this.handleError);
  }

  deleteArticle(articleId) {
    return this.axios
      .delete(`/articles/${articleId}`)
      .then(() => articleId)
      .catch(this.handleError);
  }

  createArticle(articleData) {
    return this.axios
      .post('/articles', articleData)
      .then(this.handleSuccess)
      .then((article) => new Article(article))
      .catch(this.handleError);
  }

  getComments(headers) {
    return this.axios
      .get('/comments', { headers })
      .then((res) => this.handlePaginatedListSuccess(res, FlatComment))
      .catch(this.handleError);
  }

  createComment(comment, recaptcha) {
    return this.axios
      .post('/comments', Object.assign({}, comment, { recaptcha }))
      .then(this.handleSuccess)
      .then((comment) => {
        comment.comments = comment.comments.map(
          (subcomment) => new Subcomment(subcomment)
        );
        return new Comment(comment);
      })
      .catch(this.handleError);
  }

  createCommentFromAuthenticatedUser(comment, recaptcha) {
    return this.axios
      .post(
        '/comments/authenticated',
        Object.assign({}, comment, { recaptcha })
      )
      .then(this.handleSuccess)
      .then((comment) => {
        comment.comments = comment.comments.map(
          (subcomment) => new Subcomment(subcomment)
        );
        return new Comment(comment);
      })
      .catch(this.handleError);
  }

  deleteComment(commentId) {
    return this.axios
      .delete(`/comments/${commentId}`)
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  getWods(headers, searchTerm) {
    const qs = new URLSearchParams();
    if (searchTerm) {
      qs.append('term', searchTerm);
    }
    return this.axios
      .get(`/wods?${qs}`, { headers })
      .then((res) => this.handlePaginatedListSuccess(res, Wod))
      .catch(this.handleError);
  }

  getPublishedWods(headers, searchTerm) {
    const qs = new URLSearchParams();
    if (searchTerm) {
      qs.append('term', searchTerm);
    }
    return this.axios
      .get(`/wods/published?${qs}`, { headers })
      .then((res) => this.handlePaginatedListSuccess(res, Wod))
      .catch(this.handleError);
  }

  getPublishedWodsAuthenticated(
    headers,
    { term = null, dateFrom = null, dateTo = null, attendedOnly = false } = {}
  ) {
    const qs = new URLSearchParams();
    if (term) qs.append('term', term);
    if (dateFrom) qs.append('dateFrom', dateFrom);
    if (dateTo) qs.append('dateTo', dateTo);
    if (attendedOnly) qs.append('attendedOnly', attendedOnly);

    return this.axios
      .get(`/wods/published/authenticated?${qs}`, { headers })
      .then((res) => this.handlePaginatedListSuccess(res, Wod))
      .catch(this.handleError);
  }

  getWod(wodId) {
    return this.axios
      .get(`/wods/${wodId}`)
      .then(this.handleSuccess)
      .then((wod) => new Wod(wod))
      .catch(this.handleError);
  }

  getWodComments(wodId) {
    return this.axios
      .get(`/wods/${wodId}/comments`)
      .then(this.handleSuccess)
      .then((comments) =>
        comments.map((comment) => {
          comment.comments = comment.comments.map(
            (subcomment) => new Subcomment(subcomment)
          );
          return new Comment(comment);
        })
      )
      .catch(this.handleError);
  }

  createWod(wodData) {
    return this.axios
      .post('/wods', wodData)
      .then(this.handleSuccess)
      .then((wod) => new Wod(wod))
      .catch(this.handleError);
  }

  editWod(wodData) {
    return this.axios
      .put(`/wods/${wodData.id}`, wodData)
      .then(this.handleSuccess)
      .then((wod) => new Wod(wod))
      .catch(this.handleError);
  }

  deleteWod(wodId) {
    return this.axios
      .delete(`/wods/${wodId}`)
      .then(() => wodId)
      .catch(this.handleError);
  }

  getWorkoutTypeInstances() {
    return this.axios
      .get('/workout-types/instances')
      .then(this.handleSuccess)
      .then((workoutTypeInstnces) =>
        workoutTypeInstnces.map((instance) => new WorkoutTypeInstance(instance))
      )
      .catch(this.handleError);
  }

  createFile(fileData) {
    return this.axios
      .post('/files', fileData)
      .then(this.handleSuccess)
      .then((file) => new FileData(file))
      .catch(this.handleError);
  }

  getBenchmarks(headers) {
    return this.axios
      .get('/benchmarks', { headers })
      .then((res) => this.handlePaginatedListSuccess(res, Benchmark))
      .catch(this.handleError);
  }

  getBenchmark(benchmarkId) {
    return this.axios
      .get(`/benchmarks/${benchmarkId}`)
      .then(this.handleSuccess)
      .then((benchmark) => new Benchmark(benchmark))
      .catch(this.handleError);
  }

  createBenchmark(benchmarkData) {
    return this.axios
      .post('/benchmarks', benchmarkData)
      .then(this.handleSuccess)
      .then((benchmark) => new Benchmark(benchmark))
      .catch(this.handleError);
  }

  editBenchmark(benchmarkData) {
    return this.axios
      .put(`/benchmarks/${benchmarkData.id}`, benchmarkData)
      .then(this.handleSuccess)
      .then((benchmark) => new Benchmark(benchmark))
      .catch(this.handleError);
  }

  deleteBenchmark(benchmarkId) {
    return this.axios
      .delete(`/benchmarks/${benchmarkId}`)
      .then(() => benchmarkId)
      .catch(this.handleError);
  }

  getWodsResults(
    headers,
    {
      user = null,
      dateFrom = null,
      dateTo = null,
      workoutType = null,
      workoutTypeModel = null,
    } = {}
  ) {
    return this.axios
      .get('/workout-results', {
        headers,
        params: {
          user,
          dateFrom,
          dateTo,
          workoutType,
          workoutTypeModel,
        },
      })
      .then((res) =>
        this.handlePaginatedListSuccess(res, WorkoutResultForAdmin)
      )
      .catch(this.handleError);
  }

  getWodsUserResults(wodIds) {
    const params = { wodIds };
    return this.axios
      .post('/workout-results/list/wod', params)
      .then(this.handleSuccess)
      .then((workoutResults) =>
        workoutResults.map((workoutResult) => new WorkoutResult(workoutResult))
      )
      .catch(this.handleError);
  }

  getBenchmarksUserResults() {
    return this.axios
      .get('/workout-results/list/benchmark')
      .then(this.handleSuccess)
      .then((workoutResults) =>
        workoutResults.map(
          (workoutResult) => new WorkoutBenchmarkResult(workoutResult)
        )
      )
      .catch(this.handleError);
  }

  getWodWorkoutResult(userId, wodId, workoutId) {
    return this.axios
      .get('/workout-results/check-existing', {
        params: {
          user: userId,
          wod: wodId,
          workout: workoutId,
        },
      })
      .then(this.handleSuccess)
      .then((workoutResult) => new WorkoutResultForAdmin(workoutResult))
      .catch(this.handleError);
  }

  createWorkoutResult(workoutResultData) {
    return this.axios
      .post('/workout-results', workoutResultData)
      .then(this.handleSuccess)
      .then(handleUnknownWorkoutResultType)
      .catch(this.handleError);
  }

  editWorkoutResult(workoutResultData) {
    return this.axios
      .put(`/workout-results/${workoutResultData.id}`, workoutResultData)
      .then(this.handleSuccess)
      .then(handleUnknownWorkoutResultType)
      .catch(this.handleError);
  }

  deleteWorkoutResult(workoutResultId) {
    return this.axios
      .delete(`/workout-results/${workoutResultId}`)
      .then(() => workoutResultId)
      .catch(this.handleError);
  }

  getYearGoalProgress(year) {
    return this.axios
      .get('/goals/progress', { params: { year } })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  setYearGoal(year, value) {
    return this.axios
      .post('/goals', {
        year,
        value,
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  getLastWodLeaderboard() {
    return this.axios
      .get('/leaderboard/wod')
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  getWodWeeklyLeaderboard() {
    return this.axios
      .get('/leaderboard/wod/ranking')
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  getWodAttendanceLeaderboard(year) {
    return this.axios
      .get('/leaderboard/wod/attendance', { params: { year } })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }
}

const instance = new Api(process.env.RAZZLE_API_URL);

export default instance;
